import { quizConfig } from "./config/quiz";
import { settingsEndpoint } from "./config/live";
import { escapeHtml } from "./dom";
import { validateQuizConfig, type LiveQuizConfig } from "../supabase/functions/_shared/quiz-config";

type Tab = "questions" | "terpenes" | "preview";
const draftKey = "root-to-rise-quiz-settings-draft";
const sessionKey = "root-to-rise-quiz-settings-session";

function copy<T>(value: T): T { return JSON.parse(JSON.stringify(value)) as T; }
function field(label: string, path: string, value: string, multiline = false): string {
  const control = multiline
    ? `<textarea data-path="${escapeHtml(path)}" rows="4">${escapeHtml(value)}</textarea>`
    : `<input data-path="${escapeHtml(path)}" value="${escapeHtml(value)}" />`;
  return `<label class="settings-field"><span>${escapeHtml(label)}</span>${control}</label>`;
}
function option(value: string, selected: string): string {
  return `<option value="${escapeHtml(value)}" ${value === selected ? "selected" : ""}>${escapeHtml(value)}</option>`;
}

export async function mountQuizSettings(root: HTMLElement): Promise<void> {
  document.title = "Quiz settings | Root to Rise";
  root.classList.add("settings-root");
  if (!settingsEndpoint) {
    root.innerHTML = `<main class="settings-shell"><h1>Quiz settings</h1><p>The settings endpoint is not configured for this build.</p></main>`;
    return;
  }
  let published = copy(quizConfig) as LiveQuizConfig;
  let draft = copy(published);
  let tab: Tab = "questions";
  let previewReady = false;
  let status = "Loading current quiz…";
  let token = "";
  let expiresAt = 0;
  try {
    const session = JSON.parse(sessionStorage.getItem(sessionKey) ?? "null") as { token: string; expiresAt: number } | null;
    if (session && session.expiresAt > Date.now() / 1000 + 30) { token = session.token; expiresAt = session.expiresAt; }
    const response = await fetch(settingsEndpoint, { cache: "no-store" });
    if (!response.ok) throw new Error("Could not load the published quiz.");
    const payload = await response.json() as { config?: unknown };
    if (payload.config) published = validateQuizConfig(payload.config);
    const saved = JSON.parse(localStorage.getItem(draftKey) ?? "null") as { baseVersion: string; config: LiveQuizConfig } | null;
    draft = saved?.baseVersion === published.version ? saved.config : copy(published);
    status = `Published version: ${published.version}`;
  } catch (error) {
    root.textContent = error instanceof Error ? error.message : "Could not load quiz settings.";
    return;
  }

  function persist(): void {
    previewReady = false;
    localStorage.setItem(draftKey, JSON.stringify({ baseVersion: published.version, config: draft }));
    status = "Draft saved in this browser. Preview before publishing.";
  }

  function renderQuestions(): string {
    return `${draft.questions.map((question, questionIndex) => `<section class="settings-card">
      <div class="settings-card-heading"><h2>${questionIndex + 1}. ${escapeHtml(question.prompt || question.id)}</h2><button type="button" data-action="remove-question" data-index="${questionIndex}" class="text-button">Remove</button></div>
      <p class="settings-id">ID: ${escapeHtml(question.id)} · ${question.type === "terpene" ? "Terpene score" : "Product match"}</p>
      ${field("Question", `questions.${questionIndex}.prompt`, question.prompt, true)}
      <h3>Answers</h3>
      ${question.answers.map((answer, answerIndex) => `<div class="settings-answer">
        ${field("Answer text", `questions.${questionIndex}.answers.${answerIndex}.label`, answer.label)}
        <label class="settings-field"><span>${question.type === "terpene" ? "Terpene result" : "Product match"}</span>
          <select data-path="questions.${questionIndex}.answers.${answerIndex}.${question.type === "terpene" ? "terpene" : "product"}">
            ${(question.type === "terpene" ? Object.keys(draft.results) : ["calm", "sleep", "both"]).map((id) => option(id, question.type === "terpene" ? answer.terpene ?? "" : answer.product ?? "")).join("")}
          </select></label>
        ${question.type === "terpene" ? field("Score weight (1–5)", `questions.${questionIndex}.answers.${answerIndex}.weight`, String(answer.weight ?? 1)) : ""}
        <button type="button" data-action="remove-answer" data-index="${questionIndex}" data-answer="${answerIndex}" class="text-button">Remove answer</button>
      </div>`).join("")}
      <button type="button" data-action="add-answer" data-index="${questionIndex}" class="button button--secondary">Add answer</button>
    </section>`).join("")}
    <button type="button" data-action="add-question" class="button button--secondary">Add question</button>
    <p class="settings-hint">Question and answer IDs are permanent once published. When you add a new terpene, map at least one answer to it.</p>`;
  }

  function renderTerpenes(): string {
    return `${Object.entries(draft.results).map(([id, result]) => `<section class="settings-card">
      <h2>${escapeHtml(result.name || id)}</h2><p class="settings-id">ID: ${escapeHtml(id)}</p>
      ${field("Name", `results.${id}.name`, result.name)}
      ${field("Personality title", `results.${id}.title`, result.title)}
      ${field("Image caption", `results.${id}.sourceLabel`, result.sourceLabel)}
      ${field("Result description", `results.${id}.description`, result.description, true)}
      ${result.personalityLines.map((line, index) => field(`Personality line ${index + 1}`, `results.${id}.personalityLines.${index}`, line, true)).join("")}
      ${field("Found in (one per line)", `results.${id}.foundIn`, result.foundIn.join("\n"), true)}
      ${field("Botanical image URL", `results.${id}.sourceImage`, result.sourceImage)}
      ${field("Result card URL (generated after editing result copy)", `results.${id}.shareCard`, result.shareCard)}
    </section>`).join("")}
    <button type="button" data-action="add-terpene" class="button button--secondary">Add terpene</button>
    <p class="settings-hint">Host a new botanical image at an HTTPS URL, then paste it here. A share card is generated automatically for new terpenes. Existing terpene IDs stay available for earlier results.</p>`;
  }

  function renderPreview(): string {
    return `<section class="settings-card"><h2>Review all questions</h2>${draft.questions.map((question) => `<div class="settings-preview-item"><h3>${escapeHtml(question.prompt)}</h3><ul>${question.answers.map((answer) => `<li>${escapeHtml(answer.label)} → ${escapeHtml(answer.terpene ?? answer.product ?? "")} ${answer.weight && answer.weight !== 1 ? `(weight ${answer.weight})` : ""}</li>`).join("")}</ul></div>`).join("")}</section>
    <section class="settings-card"><h2>Review results</h2>${Object.values(draft.results).map((result) => `<div class="settings-preview-item"><h3>${escapeHtml(result.name)} · ${escapeHtml(result.title)}</h3><p>${escapeHtml(result.description)}</p><p>${result.personalityLines.map(escapeHtml).join(" · ")}</p><p>Found in: ${result.foundIn.map(escapeHtml).join(" · ")}</p><img src="${escapeHtml(result.sourceImage)}" alt="${escapeHtml(result.sourceLabel)}" loading="lazy" /></div>`).join("")}</section>`;
  }

  function render(): void {
    const signedIn = Boolean(token && expiresAt > Date.now() / 1000 + 30);
    const draftState = JSON.stringify(draft) === JSON.stringify(published) ? "No unpublished edits" : `Draft: ${nextVersion()} (not published)`;
    root.innerHTML = `<main class="settings-shell">
      <header class="settings-header"><a href="/?mode=standalone">← Back to quiz</a><h1>Quiz settings</h1><p>Edit questions, answers and terpene results. Changes go live only when you select Publish now.</p><p class="settings-status" role="status">Live quiz: ${escapeHtml(published.version)} · ${escapeHtml(draftState)}</p></header>
      <nav class="settings-tabs" aria-label="Settings sections">${(["questions", "terpenes", "preview"] as const).map((item) => `<button type="button" data-tab="${item}" aria-current="${tab === item ? "page" : "false"}">${item === "questions" ? "Questions & answers" : item === "terpenes" ? "Terpene results" : "Preview & publish"}</button>`).join("")}</nav>
      ${tab === "questions" ? renderQuestions() : tab === "terpenes" ? renderTerpenes() : renderPreview()}
      ${tab === "preview" ? `<section class="settings-card"><h2>Publish to the live quiz</h2><p>Check this draft, sign in, then select Publish now. Signing in alone does not publish. Wait for the “is live” confirmation before checking the quiz. Publishing also changes future result emails. The separate Squarespace education page must be edited there.</p><button type="button" data-action="check" class="button button--secondary">Check this draft</button>${previewReady ? `<p class="settings-ok">Ready to publish as version ${escapeHtml(nextVersion())}.</p>` : ""}
        <p class="settings-status" role="status">${escapeHtml(status)}</p>
        ${signedIn ? `<p>Signed in for publishing.</p><button type="button" data-action="publish" class="button" ${previewReady ? "" : "disabled"}>Publish now</button><button type="button" data-action="sign-out" class="text-button">Sign out</button>` : `<form id="settings-login"><label class="settings-field"><span>Admin email</span><input name="email" type="email" autocomplete="username" value="safia@roottorise.com.au" required /></label><label class="settings-field"><span>Password</span><input name="password" type="password" autocomplete="current-password" required /></label><button class="button" type="submit">Sign in</button></form>`}</section>` : ""}
    </main>`;
  }

  function nextVersion(): string {
    return `terpene-personality-v${Number(published.version.match(/\d+$/)?.[0] ?? "1") + 1}`;
  }

  root.addEventListener("input", (event) => {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const path = input.dataset.path;
    if (!path) return;
    const parts = path.split(".");
    let target: Record<string, unknown> = draft as unknown as Record<string, unknown>;
    for (const part of parts.slice(0, -1)) target = target[part] as Record<string, unknown>;
    const key = parts[parts.length - 1]!;
    target[key] = key === "weight" ? Number(input.value) : key === "foundIn" ? input.value.split("\n").map((value) => value.trim()).filter(Boolean) : input.value;
    if (parts[0] === "results" && key !== "shareCard") {
      const id = parts[1]!;
      draft.results[id]!.shareCard = `${settingsEndpoint}?card=${id}`;
      const cardInput = root.querySelector<HTMLInputElement>(`[data-path="results.${id}.shareCard"]`);
      if (cardInput) cardInput.value = draft.results[id]!.shareCard;
    }
    persist();
  });
  root.addEventListener("change", (event) => {
    const select = event.target as HTMLSelectElement;
    if (!select.matches("select[data-path]")) return;
    const parts = select.dataset.path!.split(".");
    let target: Record<string, unknown> = draft as unknown as Record<string, unknown>;
    for (const part of parts.slice(0, -1)) target = target[part] as Record<string, unknown>;
    target[parts[parts.length - 1]!] = select.value;
    persist();
  });
  root.addEventListener("click", async (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-action], button[data-tab]");
    if (!button) return;
    if (button.dataset.tab) { tab = button.dataset.tab as Tab; render(); return; }
    const action = button.dataset.action;
    const index = Number(button.dataset.index);
    if (action === "add-terpene") {
      const id = prompt("New terpene ID (lowercase letters, numbers and hyphens):")?.trim().toLowerCase();
      if (!id) return;
      if (!/^[a-z][a-z0-9-]{1,39}$/.test(id) || draft.results[id]) { status = "Choose a unique lowercase terpene ID."; render(); return; }
      draft.results[id] = { id, name: id, title: "New personality", sourceLabel: "Botanical source", description: "Add the approved result description.", personalityLines: ["First personality line.", "Second personality line.", "Third personality line."], foundIn: ["Botanical source"], sourceImage: "", shareCard: `${settingsEndpoint}?card=${id}` };
      persist(); render(); return;
    }
    if (action === "add-question") {
      const id = prompt("New question ID (for example q7):")?.trim().toLowerCase();
      if (!id) return;
      if (!/^[a-z][a-z0-9-]{1,39}$/.test(id) || draft.questions.some((question) => question.id === id)) { status = "Choose a unique lowercase question ID."; render(); return; }
      draft.questions.push({ id, prompt: "New question", type: "terpene", answers: [] });
      draft.tieBreakerQuestionIds.push(id);
      persist(); render(); return;
    }
    if (action === "remove-question") {
      const question = draft.questions[index];
      if (!question) return;
      draft.questions.splice(index, 1);
      draft.tieBreakerQuestionIds = draft.tieBreakerQuestionIds.filter((id) => id !== question.id);
      persist(); render(); return;
    }
    if (action === "add-answer") {
      const question = draft.questions[index];
      if (!question) return;
      const id = prompt(`Answer ID for ${question.id} (for example ${question.id}-new):`)?.trim().toLowerCase();
      if (!id) return;
      if (!/^[a-z][a-z0-9-]{1,39}$/.test(id) || draft.questions.some((item) => item.answers.some((answer) => answer.id === id))) { status = "Choose a unique lowercase answer ID."; render(); return; }
      question.answers.push({ id, label: "New answer", ...(question.type === "terpene" ? { terpene: Object.keys(draft.results)[0], weight: 1 } : { product: "calm" as const }) });
      persist(); render(); return;
    }
    if (action === "remove-answer") { draft.questions[index]?.answers.splice(Number(button.dataset.answer), 1); persist(); render(); return; }
    if (action === "check") {
      try { draft.version = nextVersion(); validateQuizConfig(draft); previewReady = true; status = "Draft passed the configuration check. Review the preview, then publish."; }
      catch (error) { previewReady = false; status = error instanceof Error ? error.message : "Draft check failed."; }
      render(); return;
    }
    if (action === "sign-out") { token = ""; expiresAt = 0; sessionStorage.removeItem(sessionKey); status = "Signed out."; render(); return; }
    if (action === "publish") {
      if (!previewReady || !token) return;
      button.disabled = true;
      try {
        const response = await fetch(settingsEndpoint!, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ action: "publish", config: draft }) });
        const payload = await response.json() as { error?: string; version?: string };
        if (!response.ok) throw new Error(payload.error ?? "Publishing failed.");
        published = copy(draft); draft = copy(published); previewReady = false; localStorage.removeItem(draftKey);
        status = `${payload.version} is live. Open the quiz in another tab to check it.`;
      } catch (error) { status = error instanceof Error ? error.message : "Publishing failed."; }
      render();
    }
  });
  root.addEventListener("submit", async (event) => {
    const form = event.target as HTMLFormElement;
    if (form.id !== "settings-login") return;
    event.preventDefault();
    const values = new FormData(form);
    try {
      const response = await fetch(settingsEndpoint!, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "login", email: values.get("email"), password: values.get("password") }) });
      const payload = await response.json() as { error?: string; token?: string; expiresAt?: number };
      if (!response.ok || !payload.token || !payload.expiresAt) throw new Error(payload.error ?? "Sign in failed.");
      token = payload.token; expiresAt = payload.expiresAt;
      sessionStorage.setItem(sessionKey, JSON.stringify({ token, expiresAt }));
      status = "Signed in. Check the draft, then publish.";
    } catch (error) { status = `${error instanceof Error ? error.message : "Sign in failed."} Check the password for safia@roottorise.com.au. Your draft is still saved in this browser.`; }
    render();
  });
  render();
}
